'use client'

// import { OptimisticSortOrder } from '@/components/OptimisticSortOrder'
import type { SettingsQueryResult } from '@/sanity.types'
import { studioUrl } from '@/sanity/lib/api'
import { resolveHref } from '@/sanity/lib/utils'
import { createDataAttribute, stegaClean } from 'next-sanity'
import Link from 'next/link'
import { FC, useEffect, useState } from 'react'
import Image from 'next/image'

interface NavbarProps {
  data: SettingsQueryResult
}

// Hamburger Menu Icon
const HamburgerIcon: FC<{ isOpen: boolean; onClick: () => void }> = ({ isOpen, onClick }) => (
  <button
    className="flex h-8 w-8 flex-col items-center justify-center lg:hidden"
    onClick={onClick}
    aria-label="Toggle menu">
    <span
      className={`block h-0.5 w-6 transform bg-white transition-transform duration-300 ${
        isOpen ? 'translate-y-1.5 rotate-45' : ''
      }`}
    />
    <span
      className={`mt-1 block h-0.5 w-6 bg-white transition-opacity duration-300 ${
        isOpen ? 'opacity-0' : ''
      }`}
    />
    <span
      className={`mt-1 block h-0.5 w-6 transform bg-white transition-transform duration-300 ${
        isOpen ? '-translate-y-1.5 -rotate-45' : ''
      }`}
    />
  </button>
)

const Logo: FC = () => (
  <Image
    src="/images/LOKA-logo-2-cropped.svg"
    alt="Logo"
    className="w-[100px] md:w-[150px] lg:w-[200px]"
    width={1718}
    height={825}
  />
)

export function Navbar(props: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const { data } = props
  const dataAttribute =
    data?._id && data?._type
      ? createDataAttribute({
          baseUrl: studioUrl,
          id: data._id,
          type: data._type,
        })
      : null

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(prev => (prev ? false : prev))
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
      document.documentElement.style.overflow = 'unset'
    }

    // Cleanup function to reset scroll on unmount
    return () => {
      document.body.style.overflow = 'unset'
      document.documentElement.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <header
        className="x-4 sticky top-0 z-10 flex flex-wrap items-center justify-between gap-x-5 px-4 py-4 backdrop-blur md:px-10 md:py-5 lg:px-16 xl:px-32"
        data-sanity={dataAttribute?.('menuItems')}>
        <div className="flex w-full items-center justify-between lg:hidden">
          <Logo />
          <HamburgerIcon isOpen={isMobileMenuOpen} onClick={toggleMobileMenu} />
        </div>
        <Link className={`hidden text-lg hover:text-gray-300 md:text-xl lg:flex`} href={'/'}>
          <Logo />
        </Link>
        <div className="hidden flex-wrap items-center gap-x-10 px-4 py-4 md:py-5 lg:flex">
          {/* <OptimisticSortOrder id={data?._id} path="menuItems"> */}
          {data?.menuItems?.map(menuItem => {
            const href = resolveHref(menuItem?._type, menuItem?.slug)
            if (!href) {
              return null
            }
            return (
              <Link
                key={menuItem._key}
                className={`text-sm font-semibold uppercase hover:text-gray-300 md:text-base`}
                data-sanity={dataAttribute?.([
                  'menuItems',
                  { _key: menuItem._key as unknown as string },
                ])}
                href={href}>
                {menuItem?.slug === null ? 'Home' : stegaClean(menuItem.title)}
              </Link>
            )
          })}
          {/* </OptimisticSortOrder> */}
          <Link
            className={`text-white`}
            target="_blank"
            href={'https://www.facebook.com/loka_landscapes/'}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5">
              <path
                className="fill-white"
                d="M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L258.2 544L258.2 398.2L205.4 398.2L205.4 320L258.2 320L258.2 286.3C258.2 199.2 297.6 158.8 383.2 158.8C399.4 158.8 427.4 162 438.9 165.2L438.9 236C432.9 235.4 422.4 235 409.3 235C367.3 235 351.1 250.9 351.1 292.2L351.1 320L434.7 320L420.3 398.2L351 398.2L351 544L480 544C515.3 544 544 515.3 544 480L544 160C544 124.7 515.3 96 480 96L160 96z"
              />
            </svg>
          </Link>
          <Link
            className={`text-white`}
            target="_blank"
            href={'https://www.instagram.com/loka_landscapes/'}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5">
              <path
                className="fill-white"
                d="M320.3 205C256.8 204.8 205.2 256.2 205 319.7C204.8 383.2 256.2 434.8 319.7 435C383.2 435.2 434.8 383.8 435 320.3C435.2 256.8 383.8 205.2 320.3 205zM319.7 245.4C360.9 245.2 394.4 278.5 394.6 319.7C394.8 360.9 361.5 394.4 320.3 394.6C279.1 394.8 245.6 361.5 245.4 320.3C245.2 279.1 278.5 245.6 319.7 245.4zM413.1 200.3C413.1 185.5 425.1 173.5 439.9 173.5C454.7 173.5 466.7 185.5 466.7 200.3C466.7 215.1 454.7 227.1 439.9 227.1C425.1 227.1 413.1 215.1 413.1 200.3zM542.8 227.5C541.1 191.6 532.9 159.8 506.6 133.6C480.4 107.4 448.6 99.2 412.7 97.4C375.7 95.3 264.8 95.3 227.8 97.4C192 99.1 160.2 107.3 133.9 133.5C107.6 159.7 99.5 191.5 97.7 227.4C95.6 264.4 95.6 375.3 97.7 412.3C99.4 448.2 107.6 480 133.9 506.2C160.2 532.4 191.9 540.6 227.8 542.4C264.8 544.5 375.7 544.5 412.7 542.4C448.6 540.7 480.4 532.5 506.6 506.2C532.8 480 541 448.2 542.8 412.3C544.9 375.3 544.9 264.5 542.8 227.5zM495 452C487.2 471.6 472.1 486.7 452.4 494.6C422.9 506.3 352.9 503.6 320.3 503.6C287.7 503.6 217.6 506.2 188.2 494.6C168.6 486.8 153.5 471.7 145.6 452C133.9 422.5 136.6 352.5 136.6 319.9C136.6 287.3 134 217.2 145.6 187.8C153.4 168.2 168.5 153.1 188.2 145.2C217.7 133.5 287.7 136.2 320.3 136.2C352.9 136.2 423 133.6 452.4 145.2C472 153 487.1 168.1 495 187.8C506.7 217.3 504 287.3 504 319.9C504 352.5 506.7 422.6 495 452z"
              />
            </svg>
          </Link>
        </div>
      </header>
      {/* Mobile Off-Canvas Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${
          isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMobileMenu}
        />

        {/* Menu Panel */}
        <div
          className={`absolute right-0 top-0 h-full w-80 max-w-full transform bg-white shadow-xl transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}>
          <div className="flex h-full flex-col">
            {/* Menu Items */}
            <nav className="flex-1 space-y-6 px-4 py-6">
              {data?.menuItems?.map(menuItem => {
                const href = resolveHref(menuItem?._type, menuItem?.slug)
                if (!href) {
                  return null
                }
                return (
                  <Link
                    key={menuItem._key}
                    className="block border-b border-gray-100 py-2 text-lg tracking-wide text-gray-700 hover:text-black"
                    data-sanity={dataAttribute?.([
                      'menuItems',
                      { _key: menuItem._key as unknown as string },
                    ])}
                    href={href}
                    onClick={closeMobileMenu}>
                    {menuItem?.slug === null ? 'Home' : stegaClean(menuItem.title)}
                  </Link>
                )
              })}
            </nav>
            {/* Social Links */}
            <div className="border-t border-gray-100 px-4 py-6">
              <div className="flex justify-center gap-6">
                <Link
                  href={'https://www.facebook.com/loka_landscapes/'}
                  className="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6">
                    <path
                      className="fill-current"
                      d="M160 96C124.7 96 96 124.7 96 160L96 480C96 515.3 124.7 544 160 544L258.2 544L258.2 398.2L205.4 398.2L205.4 320L258.2 320L258.2 286.3C258.2 199.2 297.6 158.8 383.2 158.8C399.4 158.8 427.4 162 438.9 165.2L438.9 236C432.9 235.4 422.4 235 409.3 235C367.3 235 351.1 250.9 351.1 292.2L351.1 320L434.7 320L420.3 398.2L351 398.2L351 544L480 544C515.3 544 544 515.3 544 480L544 160C544 124.7 515.3 96 480 96L160 96z"
                    />
                  </svg>
                </Link>
                <Link
                  href={'https://www.instagram.com/loka_landscapes/'}
                  className="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6">
                    <path
                      className="fill-current"
                      d="M320.3 205C256.8 204.8 205.2 256.2 205 319.7C204.8 383.2 256.2 434.8 319.7 435C383.2 435.2 434.8 383.8 435 320.3C435.2 256.8 383.8 205.2 320.3 205zM319.7 245.4C360.9 245.2 394.4 278.5 394.6 319.7C394.8 360.9 361.5 394.4 320.3 394.6C279.1 394.8 245.6 361.5 245.4 320.3C245.2 279.1 278.5 245.6 319.7 245.4zM413.1 200.3C413.1 185.5 425.1 173.5 439.9 173.5C454.7 173.5 466.7 185.5 466.7 200.3C466.7 215.1 454.7 227.1 439.9 227.1C425.1 227.1 413.1 215.1 413.1 200.3zM542.8 227.5C541.1 191.6 532.9 159.8 506.6 133.6C480.4 107.4 448.6 99.2 412.7 97.4C375.7 95.3 264.8 95.3 227.8 97.4C192 99.1 160.2 107.3 133.9 133.5C107.6 159.7 99.5 191.5 97.7 227.4C95.6 264.4 95.6 375.3 97.7 412.3C99.4 448.2 107.6 480 133.9 506.2C160.2 532.4 191.9 540.6 227.8 542.4C264.8 544.5 375.7 544.5 412.7 542.4C448.6 540.7 480.4 532.5 506.6 506.2C532.8 480 541 448.2 542.8 412.3C544.9 375.3 544.9 264.5 542.8 227.5zM495 452C487.2 471.6 472.1 486.7 452.4 494.6C422.9 506.3 352.9 503.6 320.3 503.6C287.7 503.6 217.6 506.2 188.2 494.6C168.6 486.8 153.5 471.7 145.6 452C133.9 422.5 136.6 352.5 136.6 319.9C136.6 287.3 134 217.2 145.6 187.8C153.4 168.2 168.5 153.1 188.2 145.2C217.7 133.5 287.7 136.2 320.3 136.2C352.9 136.2 423 133.6 452.4 145.2C472 153 487.1 168.1 495 187.8C506.7 217.3 504 287.3 504 319.9C504 352.5 506.7 422.6 495 452z"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
